import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, X, Clock, FileText, ArrowRight, Star } from 'lucide-react';
// Design Tokens
const COLORS = {
  bg: '#FDF8F3',
  accent: '#C75D3A',
  text: '#2D2A26',
  textLight: '#5C5550',
  cardBg: '#FFFFFF',
  highlight: '#F2E6D8' // Light beige for highlights
};
// Animation variants
const fadeIn = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: 'easeOut'
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};
export function SalesPage() {
  const [modalOpen, setModalOpen] = useState<'privacy' | 'terms' | null>(null);
  const closeModal = () => setModalOpen(null);
  return <div className="min-h-screen w-full font-sans selection:bg-[#C75D3A] selection:text-white" style={{
    backgroundColor: COLORS.bg,
    color: COLORS.text
  }} data-id="element-2">
      {/* Modal */}
      <AnimatePresence data-id="element-3">
        {modalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={closeModal} data-id="element-4">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.2
        }} className="bg-white rounded-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()} data-id="element-5">
              <div className="flex items-center justify-between p-6 border-b border-[#F2E6D8]" data-id="element-6">
                <h2 className="text-2xl font-serif font-bold text-[#2D2A26]" data-id="element-7">
                  {modalOpen === 'privacy' ? 'Privacy Policy' : 'Terms of Service'}
                </h2>
                <button onClick={closeModal} className="p-2 rounded-full hover:bg-[#FDF8F3] transition-colors" data-id="element-8">
                  <X className="w-5 h-5 text-[#5C5550]" data-id="element-9" />
                </button>
              </div>
              <div className="p-6 overflow-y-auto max-h-[60vh] text-[#5C5550] space-y-4 text-sm leading-relaxed" data-id="element-10">
                {modalOpen === 'privacy' ? <>
                    <p className="font-medium text-[#2D2A26]" data-id="element-11">
                      Last updated: {new Date().toLocaleDateString()}
                    </p>

                    <h3 className="font-bold text-[#2D2A26] mt-6" data-id="element-12">
                      Information We Collect
                    </h3>
                    <p data-id="element-13">
                      When you purchase our digital products, we collect only
                      the information necessary to process your order, including
                      your email address and payment information (processed
                      securely through our payment provider).
                    </p>

                    <h3 className="font-bold text-[#2D2A26] mt-6" data-id="element-14">
                      How We Use Your Information
                    </h3>
                    <p data-id="element-15">
                      We use your information solely to deliver your purchased
                      products and send important updates about your purchase.
                      We do not sell, rent, or share your personal information
                      with third parties for marketing purposes.
                    </p>

                    <h3 className="font-bold text-[#2D2A26] mt-6" data-id="element-16">Cookies</h3>
                    <p data-id="element-17">
                      Our website may use cookies to improve your browsing
                      experience. You can disable cookies in your browser
                      settings at any time.
                    </p>

                    <h3 className="font-bold text-[#2D2A26] mt-6" data-id="element-18">
                      Data Security
                    </h3>
                    <p data-id="element-19">
                      We implement appropriate security measures to protect your
                      personal information. However, no method of transmission
                      over the internet is 100% secure.
                    </p>

                    <h3 className="font-bold text-[#2D2A26] mt-6" data-id="element-20">
                      Your Rights
                    </h3>
                    <p data-id="element-21">
                      You have the right to access, correct, or delete your
                      personal information. You may also unsubscribe from our
                      emails at any time using the link provided in each email.
                    </p>

                    <h3 className="font-bold text-[#2D2A26] mt-6" data-id="element-22">
                      Changes to This Policy
                    </h3>
                    <p data-id="element-23">
                      We may update this privacy policy from time to time. Any
                      changes will be posted on this page with an updated
                      revision date.
                    </p>
                  </> : <>
                    <p className="font-medium text-[#2D2A26]" data-id="element-24">
                      Last updated: {new Date().toLocaleDateString()}
                    </p>

                    <h3 className="font-bold text-[#2D2A26] mt-6" data-id="element-25">
                      Agreement to Terms
                    </h3>
                    <p data-id="element-26">
                      By purchasing and using "Stress-Free Side Hustles for
                      Women," you agree to be bound by these Terms of Service.
                      If you do not agree with any part of these terms, please
                      do not purchase or use our products.
                    </p>

                    <h3 className="font-bold text-[#2D2A26] mt-6" data-id="element-27">
                      Digital Product Delivery
                    </h3>
                    <p data-id="element-28">
                      Upon successful payment, you will receive immediate access
                      to download your digital product. Due to the digital
                      nature of this product, all sales are final.
                    </p>

                    <h3 className="font-bold text-[#2D2A26] mt-6" data-id="element-29">
                      Intellectual Property
                    </h3>
                    <p data-id="element-30">
                      All content included in this product is the intellectual
                      property of Stress-Free Side Hustles. You may not
                      reproduce, distribute, or resell any part of this product
                      without written permission.
                    </p>

                    <h3 className="font-bold text-[#2D2A26] mt-6" data-id="element-31">
                      Personal Use License
                    </h3>
                    <p data-id="element-32">
                      Your purchase grants you a personal, non-transferable
                      license to use the materials for your own business
                      purposes. You may not share, distribute, or resell the
                      product or its contents.
                    </p>

                    <h3 className="font-bold text-[#2D2A26] mt-6" data-id="element-33">
                      Disclaimer
                    </h3>
                    <p data-id="element-34">
                      The information provided in this product is for
                      educational purposes only. Results may vary based on
                      individual effort, skills, and market conditions. We do
                      not guarantee any specific income or results.
                    </p>

                    <h3 className="font-bold text-[#2D2A26] mt-6" data-id="element-35">
                      Limitation of Liability
                    </h3>
                    <p data-id="element-36">
                      We shall not be liable for any indirect, incidental, or
                      consequential damages arising from your use of this
                      product.
                    </p>

                    <h3 className="font-bold text-[#2D2A26] mt-6" data-id="element-37">
                      Changes to Terms
                    </h3>
                    <p data-id="element-38">
                      We reserve the right to modify these terms at any time.
                      Continued use of the product after changes constitutes
                      acceptance of the new terms.
                    </p>
                  </>}
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* 1. HERO SECTION */}
      <section className="relative px-6 pt-16 pb-20 md:pt-24 md:pb-32 overflow-hidden" data-id="element-39">
        <div className="max-w-4xl mx-auto text-center" data-id="element-40">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="space-y-8" data-id="element-41">
            <span className="inline-block px-4 py-1.5 rounded-full text-sm font-medium tracking-wide uppercase bg-[#F2E6D8] text-[#C75D3A]" data-id="element-42">
              For women who are overloaded
            </span>

            <h1 className="text-4xl md:text-6xl font-serif font-bold leading-tight md:leading-tight text-[#2D2A26]" data-id="element-43">
              A 30-Day Side Hustle Plan for Women Who Are{' '}
              <span className="text-[#C75D3A] italic" data-id="element-44">Already Tired.</span>
            </h1>

            <div className="max-w-2xl mx-auto space-y-6 text-lg md:text-xl text-[#5C5550] leading-relaxed" data-id="element-45">
              <p data-id="element-46">
                You don't need a "dream business." You need something smaller.
                Calmer. Real.
              </p>
              <p data-id="element-47">
                Because when your life is already full, "start a side hustle"
                doesn't sound exciting. It sounds like another thing you'll
                begin… and quietly abandon.
              </p>
            </div>

            <div className="pt-8 pb-12" data-id="element-48">
              <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={() => {
              document.getElementById('pricing')?.scrollIntoView({
                behavior: 'smooth'
              });
            }} className="bg-[#C75D3A] text-white px-8 py-4 rounded-lg text-lg font-semibold shadow-lg hover:bg-[#B04D2E] transition-colors flex items-center gap-2 mx-auto" data-id="element-49">
                Get the Playbook <ArrowRight className="w-5 h-5" data-id="element-50" />
              </motion.button>
              <p className="mt-4 text-sm text-[#5C5550] opacity-80" data-id="element-51">
                Instant PDF Download • 30-Day Plan
              </p>
            </div>

            <motion.div className="relative mx-auto max-w-3xl rounded-2xl overflow-hidden shadow-2xl border-4 border-white" initial={{
            opacity: 0,
            y: 40
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2,
            duration: 0.8
          }} data-id="element-52">
              <img src="https://cdn.magicpatterns.com/uploads/fi35db319MuMDDHQvogbVK/Side_Hustle.png" alt="Stress-Free Side Hustles for Women Playbook on devices" className="w-full h-auto object-cover" data-id="element-53" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 2. PAIN POINTS SECTION */}
      <section className="px-6 py-20 md:py-32 bg-white" data-id="element-54">
        <div className="max-w-3xl mx-auto" data-id="element-55">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="space-y-12" data-id="element-56">
            <div className="space-y-6" data-id="element-57">
              <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#2D2A26]" data-id="element-58">
                The situation you're in <br data-id="element-59" />
                <span className="text-[#5C5550] text-2xl md:text-3xl font-normal" data-id="element-60">
                  (and why it feels so hard)
                </span>
              </h2>

              <p className="text-lg text-[#5C5550] leading-relaxed" data-id="element-61">
                You've probably tried this already. You saved the videos. You
                screenshotted the "side hustle ideas." You made a note in your
                phone with 12 different directions.
              </p>

              <p className="text-lg text-[#5C5550] leading-relaxed font-medium" data-id="element-62">
                And then real life happened.
              </p>
            </div>

            <div className="grid gap-6 md:grid-cols-2" data-id="element-63">
              <div className="bg-[#FDF8F3] p-8 rounded-xl border border-[#F2E6D8]" data-id="element-64">
                <h3 className="font-serif text-xl font-bold mb-4 text-[#C75D3A]" data-id="element-65">
                  The Reality
                </h3>
                <p className="text-[#5C5550]" data-id="element-66">
                  Work. Family. A brain that's already doing 37 tabs at once.
                </p>
              </div>
              <div className="bg-[#FDF8F3] p-8 rounded-xl border border-[#F2E6D8]" data-id="element-67">
                <h3 className="font-serif text-xl font-bold mb-4 text-[#C75D3A]" data-id="element-68">
                  The Wall
                </h3>
                <ul className="space-y-3 text-[#5C5550]" data-id="element-69">
                  <li className="flex items-start gap-3" data-id="element-70">
                    <X className="w-5 h-5 text-[#C75D3A] shrink-0 mt-0.5" data-id="element-71" />
                    <span data-id="element-72">You don't know what to pick</span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-73">
                    <X className="w-5 h-5 text-[#C75D3A] shrink-0 mt-0.5" data-id="element-74" />
                    <span data-id="element-75">You don't want to waste time</span>
                  </li>
                  <li className="flex items-start gap-3" data-id="element-76">
                    <X className="w-5 h-5 text-[#C75D3A] shrink-0 mt-0.5" data-id="element-77" />
                    <span data-id="element-78">You don't want to look cringe online</span>
                  </li>
                </ul>
              </div>
            </div>

            <p className="text-xl text-center font-serif italic text-[#2D2A26]" data-id="element-79">
              So you don't move. Not because you don't want the money. <br data-id="element-80" />
              Because you don't trust the process.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 3. WHY OLD SOLUTIONS FAIL */}
      <section className="px-6 py-20 md:py-32" style={{
      backgroundColor: COLORS.bg
    }} data-id="element-81">
        <div className="max-w-3xl mx-auto" data-id="element-82">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="space-y-10" data-id="element-83">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#2D2A26]" data-id="element-84">
              Why most side hustle advice fails <br data-id="element-85" />
              <span className="text-[#C75D3A]" data-id="element-86">
                (especially for busy women)
              </span>
            </h2>

            <div className="prose prose-lg text-[#5C5550]" data-id="element-87">
              <p data-id="element-88">
                Most side hustle advice is built for a{' '}
                <strong data-id="element-89">fantasy person</strong>: unlimited time, unlimited
                energy, and a high tolerance for being inconsistent.
              </p>
              <p data-id="element-90">
                It tells you to "build your brand," "find your passion," and
                "create content." But for you, that turns into:
              </p>
            </div>

            <div className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border border-[#F2E6D8]" data-id="element-91">
              <ul className="space-y-4" data-id="element-92">
                {['Endless researching', 'Overthinking the idea', 'Overbuilding (logos, websites, perfect aesthetics)', 'And still no first sale'].map((item, i) => <li key={i} className="flex items-center gap-4 text-lg text-[#5C5550]" data-id="element-93">
                    <div className="w-2 h-2 rounded-full bg-[#C75D3A]" data-id="element-94" />
                    {item}
                  </li>)}
              </ul>
            </div>

            <p className="text-2xl font-serif font-bold text-center text-[#2D2A26]" data-id="element-95">
              The problem isn't you. <br data-id="element-96" />
              The problem is the path.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 4. WHAT YOU ACTUALLY NEED */}
      <section className="px-6 py-20 bg-[#2D2A26] text-[#FDF8F3]" data-id="element-97">
        <div className="max-w-3xl mx-auto text-center" data-id="element-98">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="space-y-8" data-id="element-99">
            <h2 className="text-3xl font-serif font-bold" data-id="element-100">
              What you actually need: <br data-id="element-101" />
              <span className="text-[#C75D3A]" data-id="element-102">
                Fewer decisions, faster proof.
              </span>
            </h2>

            <div className="grid sm:grid-cols-2 gap-6 text-left max-w-2xl mx-auto mt-12" data-id="element-103">
              {['A small offer you can finish', "A way to test demand without 'starting a business'", "Scripts so you don't have to invent words", "A micro schedule that works when you're tired"].map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-104">
                  <Check className="w-6 h-6 text-[#C75D3A] shrink-0" data-id="element-105" />
                  <span className="text-lg opacity-90" data-id="element-106">{item}</span>
                </div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* 5. PRODUCT INTRO */}
      <section className="px-6 py-20 md:py-32 bg-white" data-id="element-107">
        <div className="max-w-4xl mx-auto text-center" data-id="element-108">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="space-y-8" data-id="element-109">
            <p className="text-[#C75D3A] font-bold tracking-widest uppercase text-sm" data-id="element-110">
              Introducing
            </p>
            <h2 className="text-4xl md:text-6xl font-serif font-bold text-[#2D2A26]" data-id="element-111">
              Stress-Free Side Hustles <br data-id="element-112" /> for Women
            </h2>
            <p className="text-xl md:text-2xl text-[#5C5550] max-w-2xl mx-auto leading-relaxed" data-id="element-113">
              A PDF playbook built to get you from "I want extra income" to{' '}
              <strong data-id="element-114">my first paid sale</strong>—in 30 days—without hustle
              culture.
            </p>

            <div className="inline-block bg-[#FDF8F3] px-6 py-3 rounded-lg border border-[#F2E6D8] text-[#C75D3A] font-medium" data-id="element-115">
              It's not a motivational ebook. It's a shipping system.
            </div>
          </motion.div>
        </div>
      </section>

      {/* 6. UNIQUE MECHANISM */}
      <section className="px-6 py-20" style={{
      backgroundColor: COLORS.bg
    }} data-id="element-116">
        <div className="max-w-5xl mx-auto" data-id="element-117">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-16" data-id="element-118">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#2D2A26] mb-6" data-id="element-119">
              How it works
            </h2>
            <p className="text-lg text-[#5C5550]" data-id="element-120">
              Instead of asking you to "figure it out," you'll use:
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8" data-id="element-121">
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2
          }} className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border-t-4 border-[#C75D3A]" data-id="element-122">
              <div className="bg-[#FDF8F3] w-14 h-14 rounded-full flex items-center justify-center mb-6" data-id="element-123">
                <FileText className="w-7 h-7 text-[#C75D3A]" data-id="element-124" />
              </div>
              <h3 className="text-2xl font-serif font-bold mb-4 text-[#2D2A26]" data-id="element-125">
                Quick-Launch Templates
              </h3>
              <p className="text-[#5C5550] leading-relaxed" data-id="element-126">
                Plug-and-play assets including offer one-liners, deliverable
                lists, hook scripts, and DM flows. Remove 90% of the guessing.
              </p>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4
          }} className="bg-white p-8 md:p-10 rounded-2xl shadow-sm border-t-4 border-[#2D2A26]" data-id="element-127">
              <div className="bg-[#FDF8F3] w-14 h-14 rounded-full flex items-center justify-center mb-6" data-id="element-128">
                <Clock className="w-7 h-7 text-[#2D2A26]" data-id="element-129" />
              </div>
              <h3 className="text-2xl font-serif font-bold mb-4 text-[#2D2A26]" data-id="element-130">
                Micro-Time Blocks
              </h3>
              <p className="text-[#5C5550] leading-relaxed" data-id="element-131">
                15–30 minute daily actions. A schedule that respects your energy
                levels so you don't need a miracle to stay consistent.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 7. OUTCOMES */}
      <section className="px-6 py-20 bg-white" data-id="element-132">
        <div className="max-w-4xl mx-auto" data-id="element-133">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} data-id="element-134">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-[#2D2A26] mb-12 text-center" data-id="element-135">
              By the end of 30 days, you'll have:
            </h2>

            <div className="grid md:grid-cols-2 gap-x-12 gap-y-6" data-id="element-136">
              {['A clear "cash problem" you\'re solving', "A low-ticket offer that's easy to explain", 'A simple delivery method (no complicated tech)', 'Reusable hooks and captions for Meta + TikTok', "A calm DM process that doesn't feel gross", 'A tracker that keeps you consistent without obsessing'].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              y: 10
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.1
            }} className="flex items-start gap-4 p-4 rounded-lg hover:bg-[#FDF8F3] transition-colors" data-id="element-137">
                  <div className="bg-[#C75D3A] rounded-full p-1 mt-1 shrink-0" data-id="element-138">
                    <Check className="w-3 h-3 text-white" data-id="element-139" />
                  </div>
                  <span className="text-lg text-[#5C5550] font-medium" data-id="element-140">
                    {item}
                  </span>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* 8. WHAT'S INSIDE */}
      <section className="px-6 py-20 md:py-32 bg-[#2D2A26] text-[#FDF8F3]" data-id="element-141">
        <div className="max-w-4xl mx-auto" data-id="element-142">
          <div className="text-center mb-16" data-id="element-143">
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4" data-id="element-144">
              What's inside
            </h2>
            <p className="opacity-80 text-lg" data-id="element-145">
              Everything you need to stop planning and start shipping.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-8" data-id="element-146">
            {[{
            title: 'The Stress-Free Rule',
            desc: 'Less thinking, more shipping.'
          }, {
            title: '3 Side Hustle Types',
            desc: "That win when you're busy."
          }, {
            title: '15-Minute Market Test',
            desc: 'Stop guessing if it will sell.'
          }, {
            title: 'Offer-in-a-Box Builder',
            desc: 'Keep it low drama.'
          }, {
            title: 'Pricing Rules',
            desc: 'Prevent you from spiraling.'
          }, {
            title: 'Posting Scripts',
            desc: 'That work without going viral.'
          }, {
            title: 'DM Scripts',
            desc: "For the 'interested but nervous' buyer."
          }, {
            title: '30-Day Micro Schedule',
            desc: "So you don't disappear."
          }, {
            title: 'Bonus Pack',
            desc: 'Quiz, swipe files, scripts, trackers.'
          }].map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            scale: 0.95
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.05
          }} className="bg-[#3E3A35] p-6 rounded-xl border border-[#5C5550]" data-id="element-147">
                <h3 className="text-[#C75D3A] font-bold mb-2" data-id="element-148">
                  {feature.title}
                </h3>
                <p className="text-sm opacity-80 leading-relaxed" data-id="element-149">
                  {feature.desc}
                </p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* 9. WHO THIS IS FOR */}
      <section className="px-6 py-20 md:py-32 bg-[#FDF8F3]" data-id="element-150">
        <div className="max-w-5xl mx-auto" data-id="element-151">
          <div className="grid md:grid-cols-2 gap-12" data-id="element-152">
            {/* For You */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-white p-8 md:p-12 rounded-2xl shadow-sm" data-id="element-153">
              <h3 className="text-2xl font-serif font-bold text-[#2D2A26] mb-8 flex items-center gap-3" data-id="element-154">
                <span className="bg-[#D1FAE5] text-[#059669] p-2 rounded-full" data-id="element-155">
                  <Check className="w-5 h-5" data-id="element-156" />
                </span>
                This is for you if:
              </h3>
              <ul className="space-y-4" data-id="element-157">
                {['You want extra income but your life is already full', "You're tired of advice that assumes unlimited time", 'You want something you can sell without becoming an influencer', 'You want a simple plan you can follow on low energy'].map((item, i) => <li key={i} className="flex items-start gap-3 text-[#5C5550]" data-id="element-158">
                    <Check className="w-5 h-5 text-[#059669] shrink-0 mt-0.5" data-id="element-159" />
                    <span data-id="element-160">{item}</span>
                  </li>)}
              </ul>
            </motion.div>

            {/* Not For You */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-white p-8 md:p-12 rounded-2xl shadow-sm opacity-80" data-id="element-161">
              <h3 className="text-2xl font-serif font-bold text-[#2D2A26] mb-8 flex items-center gap-3" data-id="element-162">
                <span className="bg-[#FEE2E2] text-[#DC2626] p-2 rounded-full" data-id="element-163">
                  <X className="w-5 h-5" data-id="element-164" />
                </span>
                This is NOT for you if:
              </h3>
              <ul className="space-y-4" data-id="element-165">
                {['You want instant money without doing anything', "You're looking for complex investing/ecom systems", 'You want a "build for 6 months then launch" approach'].map((item, i) => <li key={i} className="flex items-start gap-3 text-[#5C5550]" data-id="element-166">
                    <X className="w-5 h-5 text-[#DC2626] shrink-0 mt-0.5" data-id="element-167" />
                    <span data-id="element-168">{item}</span>
                  </li>)}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 10. FINAL CTA */}
      <section className="px-6 py-20 md:py-32 bg-white text-center" data-id="element-169">
        <div className="max-w-3xl mx-auto" data-id="element-170">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="space-y-10" data-id="element-171">
            <h2 className="text-3xl md:text-5xl font-serif font-bold text-[#2D2A26]" data-id="element-172">
              The quiet truth
            </h2>

            <div className="text-xl md:text-2xl text-[#5C5550] space-y-6 leading-relaxed" data-id="element-173">
              <p data-id="element-174">You don't need more ideas.</p>
              <p data-id="element-175">
                You need one finished thing. <br data-id="element-176" />
                One offer. One post. One DM conversation. One sale.
              </p>
              <p className="font-serif italic text-[#C75D3A]" data-id="element-177">
                That first sale changes your nervous system. <br data-id="element-178" />
                Because it proves: this is real.
              </p>
            </div>

            <div id="pricing" className="bg-[#FDF8F3] p-8 md:p-12 rounded-3xl border-2 border-[#F2E6D8] mt-12 scroll-mt-8" data-id="element-179">
              <h3 className="text-2xl font-bold text-[#2D2A26] mb-4" data-id="element-180">
                Ready to stop researching and start shipping?
              </h3>
              <p className="text-[#5C5550] mb-6" data-id="element-181">
                Grab <strong data-id="element-182">Stress-Free Side Hustles for Women</strong> and
                follow the 30-day micro plan. No hustle culture. No
                brand-building detour. Just a calm system that gets you
                moving—starting tonight.
              </p>

              <div className="mb-8" data-id="element-183">
                <div className="flex items-center justify-center gap-3 mb-2" data-id="element-184">
                  <span className="text-2xl text-[#5C5550] line-through opacity-60" data-id="element-185">
                    $19.99
                  </span>
                  <span className="bg-[#D1FAE5] text-[#059669] text-sm font-bold px-3 py-1 rounded-full" data-id="element-186">
                    68% OFF
                  </span>
                </div>
                <span className="text-5xl md:text-6xl font-bold text-[#C75D3A]" data-id="element-187">
                  $6.39
                </span>
                <p className="text-sm text-[#5C5550] mt-2" data-id="element-188">
                  One-time payment • Instant access
                </p>
              </div>

              <motion.button whileHover={{
              scale: 1.02
            }} whileTap={{
              scale: 0.98
            }} onClick={() => {
              window.open('https://quickupstart.com//cart/51653266080026:1', '_blank');
            }} className="w-full md:w-auto bg-[#C75D3A] text-white px-10 py-5 rounded-lg text-xl font-bold shadow-xl hover:bg-[#B04D2E] transition-colors flex items-center justify-center gap-3 mx-auto cursor-pointer" data-id="element-189">
                Get the Playbook <ArrowRight className="w-6 h-6" data-id="element-190" />
              </motion.button>

              <div className="mt-6 flex items-center justify-center gap-2 text-sm text-[#5C5550] opacity-80" data-id="element-191">
                <Star className="w-4 h-4 fill-current text-[#C75D3A]" data-id="element-192" />
                <span data-id="element-193">Instant Digital Download</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 text-center text-[#5C5550] text-sm bg-[#FDF8F3]" data-id="element-194">
        <p className="opacity-60" data-id="element-195">
          © {new Date().getFullYear()} Stress-Free Side Hustles. All rights
          reserved.
        </p>
        <div className="mt-4 flex items-center justify-center gap-6" data-id="element-196">
          <button onClick={() => setModalOpen('privacy')} className="hover:text-[#C75D3A] transition-colors underline underline-offset-2" data-id="element-197">
            Privacy Policy
          </button>
          <span className="opacity-40" data-id="element-198">•</span>
          <button onClick={() => setModalOpen('terms')} className="hover:text-[#C75D3A] transition-colors underline underline-offset-2" data-id="element-199">
            Terms of Service
          </button>
        </div>
      </footer>
    </div>;
}